// File: /src/apis/geminiThinking.js
// Pure model-capability rules for Gemini's thinking config.
//
// Kept out of geminiApi.js so they can be unit tested: that module imports
// @google/genai, which is ESM-only and cannot be loaded under Jest.

export const supportsGeminiThinking = (model) => {
  const resolvedModel = typeof model === "string" ? model : model?.model;
  if (!resolvedModel) {
    return false;
  }

  // 2.5 plus every 3.x point release (3.1, 3.5, 3.6, …) exposes thinkingConfig.
  return /^gemini-(2\.5|3\.\d+)-/i.test(resolvedModel);
};

const parseGeminiGeneration = (model) => {
  const match = /^gemini-(\d+)\.(\d+)-/i.exec(model || "");
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]) };
};

// `minimal` was dropped as a thinking level in Gemini 3.7 — sending it is a
// hard error, not a silent downgrade, so a setting saved against an older
// model would otherwise fail every generation. Anything up to 3.6 still
// accepts it; later generations are assumed to follow 3.7.
export const supportsMinimalThinking = (model) => {
  const resolvedModel = typeof model === "string" ? model : model?.model;
  const generation = parseGeminiGeneration(resolvedModel);
  if (!generation) return true;
  if (generation.major > 3) return false;
  return !(generation.major === 3 && generation.minor >= 7);
};

// Clamp a thinking level the model would reject up to the lowest one it takes.
// Applied centrally in geminiApi so every call path — plan generation,
// exercise enhancement, the connection test — is covered rather than each
// call site having to remember the rule.
export const clampThinkingConfig = (model, config) => {
  const thinkingConfig = config?.thinkingConfig;
  if (thinkingConfig?.thinkingLevel !== "MINIMAL") return config;
  if (supportsMinimalThinking(model)) return config;

  return {
    ...config,
    thinkingConfig: { ...thinkingConfig, thinkingLevel: "LOW" },
  };
};
